import { useState } from 'react';
import { actions } from '../actions';
import { services } from '../services';
import { web3Services } from '../services/web3.service';
import { toast } from '../components/ui/use-toast';
import { getTransactionErrorMessage } from '../utils/errorMessages';
import { createAdminNotification } from '../services/notificationService';

/**
 * USER APPROVAL WORKFLOW - STANDARDIZED IMPLEMENTATION
 *
 * This hook provides a standardized user approval workflow that ensures both
 * on-chain (blockchain) and off-chain (backend) operations are performed consistently.
 *
 * APPROVAL FLOW:
 * ==============
 *
 * 1. VALIDATION PHASE:
 *    - Validate wallet connection and admin permissions on blockchain
 *    - Ensure user has admin role in KYC contract
 *
 * 2. ON-CHAIN PHASE (Blockchain Transactions):
 *    - KYC Verification: Call setVerificationData() on KYC contract
 *    - Compliance Update: Call updateComplianceStatus() on Compliance contract
 *    - Both transactions include metadata and timestamps
 *
 * 3. OFF-CHAIN PHASE (Backend API):
 *    - Call /user/approveInvestor API with transaction hashes
 *    - Update user status, KYC, and compliance flags in database
 *
 * ERROR HANDLING:
 * ===============
 * - Network errors trigger retries with exponential backoff
 * - Permission errors show clear messages to user
 * - Partial failures are logged and reported
 * - Rollback mechanisms should be implemented for critical failures
 *
 * USAGE:
 * ======
 * ```typescript
 * const { approveUser, approveUsers, isLoading } = useUserApproval();
 *
 * // Single user approval
 * const result = await approveUser({
 *   id: 'user123',
 *   walletAddress: '0x...',
 *   country: 'US',
 *   name: 'John Doe'
 * });
 *
 * // Bulk approval
 * const result = await approveUsers([user1, user2, user3]);
 * ```
 *
 * SECURITY CONSIDERATIONS:
 * =======================
 * - All blockchain transactions require admin role validation
 * - Transaction hashes are recorded for audit trails
 * - API calls include transaction references for consistency
 * - Wallet connection is validated before operations
 */
export interface ApprovalResult {
  success: boolean;
  transactionHashes?: {
    kycHash: string;
    complianceHash: string;
  };
  error?: string;
}

export interface UserApprovalData {
  id: string;
  walletAddress: string;
  country?: string;
  name?: string;
}

export const useUserApproval = () => {
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Approves a single user through the complete workflow
   */
  const approveUser = async (user: UserApprovalData): Promise<ApprovalResult> => {
    setIsLoading(true);

    try {
      // Step 1: Initialize blockchain connection and validate admin role
      const validation = await web3Services.validateConnection();
      if (!validation.connected || !validation.accounts?.length) {
        throw new Error('Please connect your wallet to approve users');
      }

      // Step 2: Initialize contracts
      const kycContract = await actions.getKycVerificationContract();
      const complianceContract = await actions.getComplianceRegistryContract();

      // Step 3: Validate admin role
      const adminRoleHash = await web3Services.callContractMethod<string>(
        kycContract.methods.ADMIN_ROLE(),
        { timeout: 15000, retries: 3, retryDelay: 1000 }
      );

      const hasAdminRole = await kycContract.methods
        .hasRole(adminRoleHash, validation.accounts[0])
        .call();

      if (!hasAdminRole) {
        throw new Error('Current wallet does not have admin privileges');
      }

      // Step 4: Perform KYC verification transaction
      const kycMetadata = `KYC Approved by admin: ${validation.accounts[0]} on ${new Date().toISOString()}`;
      const country = user.country || 'Unknown';
      const kycStatus = 2; // Approved status

      const kycTx = await new Promise<string>((resolve, reject) => {
        const promiEvent = kycContract.methods
          .setVerificationData(user.walletAddress, kycMetadata, country, kycStatus)
          .send({ from: validation.accounts[0] });
        promiEvent
          .on('transactionHash', (hash: string) => {
            resolve(hash);
          })
          .on('receipt', () => {
            toast({
              description: 'KYC verification completed successfully',
              variant: 'default',
            });
          })
          .on('error', (error: unknown) => {
            const errorMessage = error instanceof Error ? error.message : String(error);
            reject(new Error(`KYC verification failed: ${errorMessage}`));
          });
        promiEvent.catch(reject);
      });

      // Delay before second tx to avoid RPC rate limit (MetaMask -32005)
      await new Promise((r) => setTimeout(r, 3000));

      // Step 5: Perform compliance status update transaction
      const complianceMetadata = `Compliance Approved by admin: ${validation.accounts[0]} on ${new Date().toISOString()}`;

      const complianceTx = await new Promise<string>((resolve, reject) => {
        const promiEvent = complianceContract.methods
          .updateComplianceStatus(user.walletAddress, true, complianceMetadata)
          .send({ from: validation.accounts[0] });
        promiEvent
          .on('transactionHash', (hash: string) => {
            resolve(hash);
          })
          .on('receipt', () => {
            toast({
              description: 'Compliance status updated successfully',
              variant: 'default',
            });
          })
          .on('error', (error: unknown) => {
            const errorMessage = error instanceof Error ? error.message : String(error);
            reject(new Error(`Compliance update failed: ${errorMessage}`));
          });
        promiEvent.catch(reject);
      });

      // Step 6: Call backend API to update user status
      const apiParams = {
        user: [
          {
            id: user.id,
            isComplianceVerified: true,
            isKycVerified: true,
            status: true,
          },
        ],
        complianceHash: complianceTx,
        kycHash: kycTx,
      };

      const response = await services.post('/user/approveInvestor', apiParams);

      if (response?.status === 200) {
        toast({
          description: `User ${user.name || user.walletAddress} approved successfully`,
          variant: 'default',
        });
        await createAdminNotification({
          type: 'kyc',
          title: 'KYC & Compliance approved',
          message: `User ${user.name || user.walletAddress} was approved (KYC and compliance verified).`,
          priority: 'medium',
          userId: user.id,
        });

        return {
          success: true,
          transactionHashes: {
            kycHash: kycTx,
            complianceHash: complianceTx,
          },
        };
      } else {
        throw new Error('Backend API call failed');
      }
    } catch (error: unknown) {
      const err = error as { message?: string; code?: number; error?: { code?: number } };
      const code = err?.code ?? err?.error?.code;
      const isRateLimit = code === -32005 || /rate limit/i.test(String(err?.message ?? ''));

      toast({
        description: isRateLimit
          ? 'Request rate limited. Please wait 30–60 seconds and try again.'
          : getTransactionErrorMessage(error),
        variant: 'destructive',
      });

      const errorMessage =
        err?.message ?? (error instanceof Error ? error.message : 'Unknown error occurred');
      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Approves multiple users through the complete workflow
   */
  const approveUsers = async (users: UserApprovalData[]): Promise<ApprovalResult> => {
    setIsLoading(true);
    const results: ApprovalResult[] = [];

    try {
      for (const user of users) {
        const result = await approveUser(user);
        results.push(result);

        // If one approval fails, we could either continue or stop
        // For now, we'll continue with all approvals
      }

      const successCount = results.filter((r) => r.success).length;
      const failureCount = results.length - successCount;

      if (failureCount === 0) {
        toast({
          description: `Successfully approved ${successCount} user${successCount > 1 ? 's' : ''}`,
          variant: 'default',
        });
        return { success: true };
      } else {
        toast({
          description: `Approved ${successCount} user${successCount > 1 ? 's' : ''}, ${failureCount} failed`,
          variant: 'destructive',
        });
        return { success: false, error: `${failureCount} approval(s) failed` };
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Bulk approval failed';
      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    approveUser,
    approveUsers,
    isLoading,
  };
};
